import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import CapabilityStatementModal from "@/components/CapabilityStatementModal";
import { 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Target, 
  Wrench,
  Shield,
  Download,
  FileText
} from "lucide-react";

const values = [
  {
    icon: Wrench,
    title: "Quality Workmanship",
    description: "Every weld, every cut, every piece we fabricate is done to the highest standard. We take pride in our work."
  },
  {
    icon: Target,
    title: "Practical Solutions",
    description: "We understand real-world problems because we work alongside farmers and contractors every day."
  },
  {
    icon: Users,
    title: "Customer Focus",
    description: "Your project is our priority. We listen, we understand, and we deliver exactly what you need."
  },
  {
    icon: Shield,
    title: "Reliability",
    description: "When you need us, we're there. Whether it's a scheduled job or an emergency breakdown, count on us."
  }
];

export default function AboutPage() {
  const [isCapabilityModalOpen, setIsCapabilityModalOpen] = useState(false);
  
  return (
    <div className="page-transition pt-20" data-testid="about-page">
      <Helmet>
        <title>About PWC Fabrication and Engineering | Albany & Great Southern WA</title>
        <meta name="description" content="About PWC Fabrication and Engineering - Proudly Australian fabrication business serving Albany and the Great Southern region. Quality workmanship, practical solutions for farmers and contractors." />
        <meta name="keywords" content="about PWC Fabrication, PWC Engineering Albany, fabrication business Great Southern, Australian fabrication, welding company Albany WA" />
        <link rel="canonical" href="https://pwcengineering.com.au/about" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm">
                About Us
              </p>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase text-white mb-6">
                Built on Hard Work & Quality
              </h1>
              <p className="text-xl text-[#A3A3A3] leading-relaxed mb-6">
                PWC Fabrication & Engineering is a proudly Australian business serving 
                the Great Southern region of Western Australia. We specialise in custom 
                metal fabrication, welding services, and engineered solutions for the 
                agricultural, construction, and 4x4 industries.
              </p>
              <p className="text-lg text-[#A3A3A3] leading-relaxed">
                From our fully-equipped workshop and mobile welding rig, we deliver 
                quality workmanship that stands the test of time. Whether you need a 
                custom loader bucket, livestock infrastructure, or emergency repairs, 
                we've got you covered.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
                <img 
                  src="/IMG_6297.jpeg"
                  alt="PWC Mobile Welding Truck"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#E63946] p-6 rounded-2xl">
                <p className="text-4xl font-bold text-white">100%</p>
                <p className="text-white/80 text-sm uppercase tracking-wider">Australian Made</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capability Statement Modal */}
      <CapabilityStatementModal 
        isOpen={isCapabilityModalOpen} 
        onClose={() => setIsCapabilityModalOpen(false)} 
      />

      {/* Capability Statement Section */}
      <section id="capability-section" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="capability-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#E63946]" />
                <p className="text-[#E63946] font-semibold uppercase tracking-widest text-sm">
                  Capability Statement
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
                Our Capabilities<br />& Expertise
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">
                Download our comprehensive capability statement to learn more about 
                PWC Fabrication & Engineering's services, equipment, and expertise. 
                Perfect for contractors, project managers, and procurement teams.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Full workshop and mobile welding capabilities",
                  "Heavy-duty agricultural and industrial fabrication",
                  "Custom engineering solutions",
                  "Quality assurance and workmanship guarantee",
                  "Serving Albany and the Great Southern region"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#E63946]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="text-[#E63946]" size={14} />
                    </div>
                    <span className="text-[#F5F5F5]">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setIsCapabilityModalOpen(true)}
                className="inline-flex items-center gap-3 bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full transition-colors group"
                data-testid="about-capability-btn"
              >
                <Download className="w-5 h-5 group-hover:animate-bounce" />
                Download Capability Statement
              </button>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img 
                src="/capability_statement.png"
                alt="PWC Fabrication & Engineering Capability Statement"
                className="w-full max-w-lg lg:max-w-xl xl:max-w-2xl h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#E63946] p-4 rounded-xl">
                <Download className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm">
              What Drives Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white">
              Our Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#2A2A2A] p-8 rounded-sm card-hover text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#E63946]/10 border border-[#E63946]/30 rounded-sm mx-auto mb-6 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-[#E63946]" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-wide text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-[#A3A3A3] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#E63946] relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-black rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Ready to start your next project? Get in touch with us today for a 
            no-obligation quote and let's make it happen.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white hover:bg-gray-100 text-[#E63946] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-sm"
              data-testid="about-cta-btn"
            >
              Contact Us
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
